import { FormFieldType } from '../types'
import { PDFDocument } from 'pdf-lib'
import { Canvg } from 'canvg'
import axios from 'axios'
import { customAlphabet } from 'nanoid'
import { FREE_TEMPLATE_BUCKET_KEY_LENGTH, PAID_TEMPLATE_BUCKET_KEY_LENGTH, FREE_TEMPLATE_ID } from '@/constants/keys'
import { APP_BASE_PATH, SITE_ORIGIN, withBasePath } from '@/constants/site'
import { HeaderIcon } from '@/types'

export const mergePdfs = async (backgroundPdf: ArrayBuffer, contentPdf: ArrayBuffer, singlePageOnly: boolean) => {
  const backgroundDoc = await PDFDocument.load(backgroundPdf)
  const contentDoc = await PDFDocument.load(contentPdf)
  const contentPages = singlePageOnly ? contentDoc.getPages().slice(0, 1) : contentDoc.getPages()

  // Get the background template page
  const backgroundTemplatePage = backgroundDoc.getPage(0)
  const { width, height } = backgroundTemplatePage.getSize()

  // Create a new document to store the merged result
  const mergedDoc = await PDFDocument.create()

  // For each content page, create a new page with background and content
  for (let i = 0; i < contentPages.length; i++) {
    const contentPage = contentPages[i]

    // Copy background template to new document
    const [copiedBackgroundPage] = await mergedDoc.copyPages(backgroundDoc, [0])
    mergedDoc.addPage(copiedBackgroundPage)

    // Embed and draw content page on top of background
    const embeddedContentPage = await mergedDoc.embedPage(contentPage)
    const currentPage = mergedDoc.getPage(i)

    currentPage.drawPage(embeddedContentPage, {
      x: 0,
      y: 0,
      width,
      height,
    })
  }

  const mergedPdf = await mergedDoc.save()
  return mergedPdf
}

export function getHeaderIconSrc(meta: HeaderIcon['meta']): string {
  if (typeof meta === 'string') {
    return meta
  }
  return meta.src ?? ''
}

export const svgToPng = async (svgPath: string, width: number, height: number, pathColor?: string): Promise<string | null> => {
  if (!svgPath) {
    return null
  }

  try {
    const response = await fetch(withBasePath(svgPath))
    const svgString = await response.text()

    // Only modify the SVG string if pathColor is provided and truthy
    // If pathColor is undefined, null, or empty string, use original SVG without color overlay
    const modifiedSvgString = pathColor && pathColor.trim() !== '' 
      ? svgString.replace(/fill="[^"]*"/g, `fill="${pathColor}"`) 
      : svgString

    const canvas: HTMLCanvasElement = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (context) {
      const v = await Canvg.from(context, modifiedSvgString.trim())
      await v.render()
      const dataUri = canvas.toDataURL('image/png')
      return dataUri
    } else {
      console.error('Error: Unable to get canvas context')
      return null
    }
  } catch (error) {
    console.error('Error occurred:', error)
    return null
  }
}

export const pxToPt = (px: number): number => {
  const conversionFactor = 72 / 96
  return px * conversionFactor
}

export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null
  }
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() ?? null
  }
  return null
}

export const generateWhatsAppMessage = (
  personalDetails: FormFieldType[],
  familyDetails: FormFieldType[],
  contactDetails: FormFieldType[],
  personalDetailsTitle: string,
  familyDetailsTitle: string,
  contactDetailsTitle: string,
  customSections?: { id: string; title: string; fields: FormFieldType[] }[],
): string => {
  const formatDetails = (details: FormFieldType[]): string => {
    return details
      .filter(({ value, isHidden }) => value && !isHidden && (!Array.isArray(value) || value.length > 0))
      .map(({ label, value }) => {
        if (Array.isArray(value)) {
          return `${label}: ${value.join(', ')}`
        }
        return `${label}: ${value}`
      })
      .join('\n')
  }

  const personalMessage = `*${personalDetailsTitle}:*\n${formatDetails(personalDetails)}`
  const familyMessage = `*${familyDetailsTitle}:*\n${formatDetails(familyDetails)}`
  const contactMessage = `*${contactDetailsTitle}:*\n${formatDetails(contactDetails)}`

  let customSectionsMessage = ''
  if (customSections && customSections.length > 0) {
    customSectionsMessage = '\n\n' + customSections.map((section) => `*${section.title}:*\n${formatDetails(section.fields)}`).join('\n\n')
  }

  return `${personalMessage}\n\n${familyMessage}\n\n${contactMessage}${customSectionsMessage}`
}

export const downloadBiodataFile = async (isWebView: boolean = false, downloadUrl: string, onComplete?: () => void) => {
  try {
    if (isWebView) {
      window.AndroidWebInterface.downloadBiodata(downloadUrl)
    } else {
      const response = await axios.get(downloadUrl, {
        responseType: 'blob',
      })

      // Create a Blob from the PDF stream
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))

      // Create a temporary link and trigger the download
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'MarriageBiodata.pdf')
      document.body.appendChild(link)
      link.click()

      // Clean up
      link.remove()
      window.URL.revokeObjectURL(url)
    }
  } catch (error) {
    console.error('Error downloading the file:', error)
  } finally {
    onComplete?.()
  }
}

export const getDownloadUrl = (id: string): string => {
  if (process.env.NODE_ENV === 'development') {
    return `http://localhost:3000${APP_BASE_PATH}/downloads/${id}`
  }
  return `${SITE_ORIGIN}${APP_BASE_PATH}/downloads/${id}`
}

export const generateObjectKey = (isPaid: boolean = false): string => {
  const alphabet = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'
  const length = isPaid ? PAID_TEMPLATE_BUCKET_KEY_LENGTH : FREE_TEMPLATE_BUCKET_KEY_LENGTH
  return customAlphabet(alphabet, length)()
}

export const isPaidTemplate = (templateId: string): boolean => {
  return templateId !== FREE_TEMPLATE_ID
}
